// @givmo/app-admin — dashboard Participation panel (spec §129; EP-WEB8).
//
// The block that answers "is anyone actually using this?" — and the home for the two KPI cards the §127
// money strip displaced. Nothing was deleted in that re-rank: **Median match** lives here now, and
// charities-supported heads the Impact panel.
//
// Contents: the enrollment-rate Meter · active employees with joined-this-year · pending invites, which
// links to Employees → Invites · median match as a sub-stat.
//
// THREE FIGURES, THREE DIFFERENT BASES — hence three methodology affordances:
//
//   Enrollment rate  active employees ÷ the company's DECLARED headcount. The denominator is what the
//                    admin typed at sign-up and can edit (the host names where); nothing reconciles it against
//                    payroll. That is the only denominator the data supports, so the copy names it
//                    rather than implying a measured one. No declared headcount → an action, not 0%.
//
//   Active employees a live count of linked employees. Its YoY companion is a RETENTION-style baseline,
//                    not a historical headcount — an unlink hard-deletes the association with no
//                    tombstone, so a departed employee leaves no trace and the comparison reads biased
//                    low under churn. This panel therefore shows "joined this year" (a fact) instead of
//                    a YoY delta on the count (an artifact).
//
//   Median match     APPROVED MATCHES ONLY, same basis as every other "matched" figure. EP-WEB-B1 left
//                    this field on the union basis and this panel retitled it "Median gift" to make that
//                    honest; D-EP-31 took the other route — keep the "median match" concept and re-base
//                    the MATH — so the label reads "Median match (YTD)" again and its methodology names
//                    the exclusion. The company's own direct gifts are the Company-gave lane.
//                    ITS BASIS IS SET BY THE BACKEND: this panel only formats what
//                    `GET /admin/company/dashboard` sends, so the label and the math stay in sync only if
//                    the two ship in the same promotion round.
//
// PURELY PRESENTATIONAL BY DESIGN: the page owns every query and hands this section resolved values.

import { Button, Card, CardBody, CardHeader, Icon, Meter, Skeleton } from '@givmo/design-system';
import { MailPlus, Users } from 'lucide-react';
import type { CompanyDashboard } from '@givmo/api-client';
import { MethodologyHint } from '../../../components/methodology-hint';
import {
  enrollmentMethodology,
  enrollmentPercentLabel,
  type EnrollmentRateView,
} from '../../employees/invites-decode';
import { PORTAL_WORDS, type EmployerModuleWords } from '../../../lib/module-paths';
import { formatCount, formatUsd } from '../dashboard-decode';
import { InlineError, errorMessage } from './section-status';

export function ParticipationPanel({
  data,
  loading,
  isError,
  error,
  onRetry,
  enrollment,
  enrollmentLoading,
  enrollmentIsError,
  onEnrollmentRetry,
  pendingInvites,
  onManageInvites,
  words = PORTAL_WORDS,
}: {
  data: CompanyDashboard | undefined;
  loading: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
  /** Null when no rate can be computed (no declared headcount) — an action, never a fabricated 0%. */
  enrollment: EnrollmentRateView | null;
  enrollmentLoading: boolean;
  /**
   * The company read FAILED, which is a different thing from "no headcount set" even though both
   * leave `enrollment` null. Without this the panel tells an admin who already entered a headcount
   * to go enter it again.
   */
  enrollmentIsError: boolean;
  onEnrollmentRetry: () => void;
  /**
   * Pending-invite count, or `null` when it is genuinely unknown (the read failed, or the invite history
   * is too large to count client-side). Null renders the link WITHOUT a number — "0 pending" when we
   * simply could not count would be a claim we cannot make.
   */
  pendingInvites: number | null;
  onManageInvites: () => void;
  /** The host's words — where the headcount is edited (EP-MNT5(h)); the portal's by default. */
  words?: EmployerModuleWords;
}) {
  return (
    <Card testId="participation-panel">
      <CardHeader
        title="Participation"
        subtitle="How much of your company has joined the program"
        actions={
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            leadingIcon={<Icon icon={MailPlus} size={14} aria-hidden />}
            onClick={onManageInvites}
            testId="participation-manage-invites"
          >
            {pendingInvites !== null && pendingInvites > 0
              ? `${formatCount(pendingInvites)} pending ${pendingInvites === 1 ? 'invite' : 'invites'}`
              : 'Invite employees'}
          </Button>
        }
      />
      <CardBody>
        {loading || enrollmentLoading ? (
          <div className="flex flex-col gap-16">
            <Skeleton className="h-24 w-full rounded-lg" />
            <div className="grid grid-cols-1 gap-16 sm:grid-cols-2">
              {/* Explicit px: the shared scale has no 40 key, and Tailwind v4 would silently emit
                  160px from its own 4px base (build_and_ci.md -> px-scale traps). */}
              <Skeleton className="h-[76px] w-full rounded-lg" />
              <Skeleton className="h-[76px] w-full rounded-lg" />
            </div>
          </div>
        ) : isError ? (
          <InlineError
            message={errorMessage(error, 'Failed to load participation.')}
            onRetry={onRetry}
          />
        ) : (
          <div className="flex flex-col gap-20">
            {/* A FAILED company read is not a missing headcount. Both leave `enrollment` null, but
                "add your headcount in Settings" is wrong advice for a company that already has one —
                it sends the admin to re-enter a number that is already there. The two states are
                separated here rather than collapsed, and only the genuine unset case gets the CTA. */}
            {enrollmentIsError ? (
              <div className="flex flex-col gap-8" data-testid="participation-enrollment-error">
                <span className="font-sans text-s13 font-w500 tracking-normal text-text-secondary">
                  Enrollment rate
                </span>
                <InlineError
                  message="Could not load your company details."
                  onRetry={onEnrollmentRetry}
                />
              </div>
            ) : enrollment === null ? (
              <div className="flex flex-col gap-8" data-testid="participation-enrollment-empty">
                <span className="flex items-center gap-6 font-sans text-s13 font-w500 tracking-normal text-text-secondary">
                  Enrollment rate
                  <MethodologyHint
                    subject="Enrollment rate"
                    testId="participation-enrollment-methodology"
                  >
                    {enrollmentMethodology(enrollment, words)}
                  </MethodologyHint>
                </span>
                <p className="font-sans text-s14 leading-h15 tracking-normal text-text-secondary">
                  {words.headcountPromptDashboard}
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-6" data-testid="participation-enrollment">
                <span className="flex items-center gap-6 font-sans text-s13 font-w500 tracking-normal text-text-secondary">
                  Enrollment rate
                  <MethodologyHint
                    subject="Enrollment rate"
                    testId="participation-enrollment-methodology"
                  >
                    {enrollmentMethodology(enrollment, words)}
                  </MethodologyHint>
                </span>
                <Meter
                  value={enrollment.ratio}
                  max={1}
                  size="md"
                  tone="brand"
                  ariaLabel="Enrollment rate"
                  valueLabel={enrollmentPercentLabel(enrollment)}
                  valueText={`${enrollmentPercentLabel(enrollment)} — ${enrollment.active} of ${enrollment.declared} employees`}
                  testId="participation-enrollment-meter"
                />
                <p className="font-sans text-s13 tracking-normal text-text-tertiary">
                  {formatCount(enrollment.active)} of {formatCount(enrollment.declared)} employees
                  you told us you have
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 gap-16 sm:grid-cols-2">
              <SubStat
                testId="participation-active-employees"
                label="Active employees"
                subject="Active employees"
                value={formatCount(data?.active_employees)}
                caption={`${formatCount(data?.active_employees_joined_this_year)} joined this year`}
                icon={<Icon icon={Users} size={16} aria-hidden />}
                methodology="Employees whose Givmo account is linked to your company right now, counted live. Joined-this-year counts links made since January 1. Unlinking removes the record entirely, so this is a current count and not a history."
              />
              {/* The card the money strip displaced (§129). The testId keeps the older "gift" wording
                  on purpose — it is a contract e2e selects on, so it outlives label changes. */}
              <SubStat
                testId="participation-median-gift"
                label="Median match (YTD)"
                subject="Median match"
                value={formatUsd(data?.median_donation_match)}
                caption={`vs ${formatUsd(data?.median_prior_full_year)} prior full year`}
                methodology="The middle amount among the employee match requests your company approved this year — approved matches only. Your company's own direct gifts are counted separately under Company gave, not here. It is not an average, so one very large match does not move it."
              />
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/** One labelled figure with its methodology affordance and a caption. */
function SubStat({
  testId,
  label,
  subject,
  value,
  caption,
  methodology,
  icon,
}: {
  testId: string;
  label: string;
  subject: string;
  value: string;
  caption: string;
  methodology: string;
  icon?: React.ReactNode;
}) {
  return (
    <div
      className="flex flex-col gap-4 rounded-lg border border-border-subtle bg-surface-sunken p-16"
      data-testid={testId}
    >
      <span className="flex items-center gap-6 font-sans text-s13 font-w500 tracking-normal text-text-tertiary">
        {icon}
        {label}
        <MethodologyHint subject={subject} testId={`${testId}-methodology`}>
          {methodology}
        </MethodologyHint>
      </span>
      <span className="font-sans text-s24 font-w700 leading-h12 tracking-givmo text-text-strong tabular-nums">
        {value}
      </span>
      <span className="font-sans text-s13 tracking-normal text-text-tertiary">{caption}</span>
    </div>
  );
}
